import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useBooking } from '@features/booking/state';
import type { ServiceType } from '@features/booking/state/types';

/**
 * Valid service types for validation
 */
const VALID_SERVICE_TYPES: ServiceType[] = ['breakfast', 'brunch', 'lunch', 'dinner'];

function isValidServiceType(value: string): value is ServiceType {
  return VALID_SERVICE_TYPES.includes(value as ServiceType);
}

/**
 * Validate date string format (YYYY-MM-DD)
 */
function isValidDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value));
}

/**
 * Validate center hour format (HH:mm or HH:mm:ss)
 */
function isValidHour(value: string): boolean {
  return /^\d{1,2}:\d{2}(:\d{2})?$/.test(value);
}

/**
 * Deep-link redirect page for `/search/:date/:partySize/:serviceType/:centerHour`
 *
 * Pre-fills booking state with the URL params and navigates to the appropriate
 * step in the booking flow. Replicates the legacy AngularJS `home.search` route
 * behavior where params are extracted and the booking flow is auto-advanced.
 *
 * Also handles `/waiting-list-alternative/.../:wlResId` variant by accepting
 * an optional `wlResId` param that marks the booking as a WL rescheduling.
 */
const DeepLinkRedirectPage = () => {
  const { date, partySize, serviceType, centerHour, wlResId } = useParams<{
    date: string;
    partySize: string;
    serviceType: string;
    centerHour: string;
    wlResId?: string;
  }>();
  const navigate = useNavigate();
  const booking = useBooking();

  useEffect(() => {
    // Wait for BookingProvider to finish loading from sessionStorage
    if (booking.isLoading) return;

    // Validate required params
    if (!date || !partySize || !serviceType) {
      navigate('/', { replace: true });
      return;
    }

    const parsedPartySize = parseInt(partySize, 10);
    if (isNaN(parsedPartySize) || parsedPartySize < 1) {
      navigate('/', { replace: true });
      return;
    }

    if (!isValidDate(date)) {
      navigate('/', { replace: true });
      return;
    }

    if (!isValidServiceType(serviceType)) {
      navigate('/', { replace: true });
      return;
    }

    // Set all booking state in a single update to prevent race conditions
    // with the navigate call that follows
    const updates: Parameters<typeof booking.updateBooking>[0] = {
      diners: parsedPartySize,
      selectedDate: date,
      service: serviceType,
      timeSlots: null,
      selectedTime: null,
      columnType: null,
      specialEvent: null,
    };

    // Store center hour for auto-selection in SelectTimePage
    if (centerHour && isValidHour(centerHour)) {
      const normalized = centerHour.split(':').slice(0, 2).join(':');
      updates.deepLinkCenterHour = normalized;
    }

    // Handle waiting list alternative
    if (wlResId) {
      const parsedWlResId = parseInt(wlResId, 10);
      if (!isNaN(parsedWlResId)) {
        updates.suggestedFromWaitingListAlternatives = true;
        updates.rescheduleId = parsedWlResId;
      }
    }

    booking.updateBooking(updates);

    // Navigate to select-time (date, service, party size are pre-set)
    navigate('/select-time', { replace: true });
  }, [booking.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-brand" />
    </div>
  );
};

export default DeepLinkRedirectPage;
